import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button } from '$partials/ui';
import { formatLinkBlockToUrl } from '$utilities/format';
import { IconLogo, IconLogoMobile, IconMinus, IconPlus } from '$partials/icon';
import HeaderDialog from './header-dialog';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */
const offices = [
    {
        value: 'london',
        label: 'London'
    },
    {
        value: 'new-york',
        label: 'New York'
    },
    {
        value: 'paris',
        label: 'Paris'
    },
    {
        value: 'amsterdam',
        label: 'Amsterdam'
    }
];

const LinkBlock = (obj) => {
    return (
        <Button
            as='a'
            className='flex self-start type-style-s hover:text-text-default-secondary'
            href={formatLinkBlockToUrl(obj.link)}>
            {obj.label}
        </Button>
    );
};

/**
 * @type {import('$types/components/partials/global.d.ts').Header}
 */
const Header = (props) => {
    const [isHidden, setIsHidden] = useState(false);
    const [isFixed, setIsFixed] = useState(!props.isHomepage);
    const [isMobileFixed, setIsMobileFixed] = useState(true);
    const [isOpened, setIsOpened] = useState(false);

    const scrollPosition = useRef(0);
    const scrollDelta = useRef(0);
    const headerRef = useRef();
    const navRef = useRef();
    const itemRefs = useRef([]);
    const inputRef = useRef('');

    const handleScroll = useCallback(() => {
        const mainBody = props.bodyRef?.current;

        if (props.isHomepage) {
            const { height } = headerRef?.current?.getBoundingClientRect();
            const threshold = height * 2 || 80;
            const fadeZone = 40;

            if (mainBody?.getBoundingClientRect().top <= 0) {
                // hide logo to prevent overlap with footer
                setIsHidden(true);
            } else {
                setIsFixed(false);
                setIsHidden(false);
            }
            if (window.scrollY < scrollPosition.current) {
                // Scroll UP
                // Start revealing heading
                const progress = window.scrollY / 300;
                const fadeStart = (1 - progress) * 100;
                const fadeEnd = Math.min(fadeStart + fadeZone, 100);
                itemRefs.current.forEach(el => {
                    if (!el) { return; }
                    el.style.mask = `linear-gradient(to bottom, black 0%, black ${fadeStart}%, transparent ${fadeEnd}%, transparent 100%)`;
                });

                if (scrollPosition.current > threshold  && !isFixed && mainBody?.getBoundingClientRect().top <= 0) {
                    setIsFixed(true);
                }
                if (scrollPosition.current > threshold  && mainBody?.getBoundingClientRect().top <= -threshold){
                    setIsMobileFixed(true);
                }

            } else {
                // Scroll DOWN
                // Start fading heading
                const progress = window.scrollY / 200;
                const fadeStart = (1 - progress) * 100;
                const fadeEnd = fadeStart + fadeZone;
                itemRefs.current.forEach(el => {
                    if (!el) { return; }
                    el.style.mask = `linear-gradient(to bottom, black 0%, black ${fadeStart}%, transparent ${fadeEnd}%)`;
                });

                if (scrollPosition.current < threshold) {
                    setIsFixed(false);
                }
                if (scrollPosition.current > threshold && isMobileFixed && mainBody?.getBoundingClientRect().top <= 5) {
                    setIsMobileFixed(false);
                }
            }

        } else {
            const { height } = navRef?.current?.getBoundingClientRect();
            const threshold = height;

            if (window.scrollY < scrollPosition.current) {
                // Scroll UP
                // Add scroll delta
                scrollDelta.current += (window.scrollY - scrollPosition.current);

                if (Math.abs(scrollDelta.current) > threshold) {
                    setIsFixed(true);
                    scrollDelta.current = 0;
                }

                setIsMobileFixed(true);
            } else {
                // Scroll DOWN
                if (isFixed) {
                    scrollDelta.current -= (window.scrollY - scrollPosition.current);
                }

                if (mainBody?.getBoundingClientRect().top <= 40){
                    setIsMobileFixed(false);
                }

                // Hide header if scrollThreshold's negative value is reached

                if (scrollDelta.current < -threshold && mainBody?.getBoundingClientRect().top <= threshold) {
                    setIsFixed(false);
                    scrollDelta.current = 0;
                } else if (window.scrollY > threshold && (window.scrollY + 1) >= scrollPosition.current && scrollDelta.current < -threshold) {
                    // Reseting value between states
                    scrollDelta.current = 0;
                }
            }
        }

        // update current scroll pos
        scrollPosition.current = window.scrollY;
    }, []);

    const handleSearch = (event) => {
        if (event.key === 'Enter') {
            window.location = `${props.domain}/search?term=${event.target.value}`;
        }
    };

    useEffect(() => {
        document.addEventListener('scroll', () => handleScroll());
        return () => {
            document.removeEventListener('scroll', () => handleScroll());
        };
    }, []);

    return (
        <>
            {/* Mobile */}
            <div className={`${!isMobileFixed ? 'opacity-0' : ''} ${props.isHomepage && !isHidden ? 'z-10' : 'z-50'} sm:hidden sticky top-0 flex flex-col overflow-hidden w-dvw`}>
                <div className='flex flex-col gap-y-[79px] bg-white px-8 pt-8 pb-16'>
                    <div className={`${isHidden || !props.isHomepage ? 'justify-between' : 'justify-end'} flex flex-row w-full`}>
                        <div className={`${!isHidden && props.isHomepage ? 'hidden' : 'flex'} w-2/6`}>
                            <a
                                className='w-full h-full'
                                href={`/?office=${props.office || 'london'}`}>
                                <IconLogoMobile/>
                            </a>
                        </div>
                        <Button
                            className='flex self-start absolute top-8 right-8'
                            onClick={()=> {setIsOpened(true);}}
                            renderIcon={<IconPlus/>} />
                    </div>
                    {props.isHomepage && !isHidden && <IconLogoMobile/>}
                </div>
                <HeaderDialog
                    handleClose={()=>setIsOpened(false)}
                    isNavOpen={isOpened}>
                    <div className='flex flex-col gap-y-[64px] bg-white px-8 pt-8 pb-16'>
                        <Button
                            className='flex self-end'
                            onClick={()=> {setIsOpened(!isOpened);}}
                            renderIcon={<IconMinus/>} />
                        <a href={`/?office=${props.office || 'london'}`}>
                            <IconLogoMobile/>
                        </a>
                    </div>
                    <div className='-mx-layout-col bg-white flex-grow mt-42'>
                        <div className='px-layout-outer flex h-full flex-wrap justify-between'>
                            <div className='flex w-full flex-wrap gap-y-24'>
                                <div className='w-3/7 px-layout-col'>
                                    <LinkBlock {...props.heading1?.[0]}/>
                                    <div dangerouslySetInnerHTML={{ __html: props.column1 }} />
                                </div>
                                <div className='w-3/7 px-layout-col'>
                                    <LinkBlock {...props.heading2?.[0]}/>
                                    <div dangerouslySetInnerHTML={{ __html: props.column2 }} />
                                </div>
                                <div
                                    className='w-3/7 px-layout-col'>
                                    <div className='flex flex-col'>
                                        {offices.map(office => {
                                            return (
                                                <a
                                                    href={`/?office=${office.value}`}
                                                    key={office.value}>
                                                    {office.label}
                                                </a>
                                            );
                                        })}
                                    </div>
                                    <div/>
                                </div>
                                <div className='w-full px-layout-col mt-28 relative'>
                                    <input
                                        className='w-full type-style-s border-b border-black/20 pb-5 rounded-none'
                                        onKeyDown={handleSearch}
                                        ref={inputRef}/>
                                    <Button
                                        as='submit'
                                        className='absolute right-8 type-style-s top-4'
                                        onClick={() => {
                                            if (inputRef?.current?.value){
                                                window.location = `${props.domain}/search?term=${inputRef.current.value}`;
                                            }
                                        }}>
                                        {'Search'}
                                    </Button>
                                </div>
                            </div>
                            <div className='w-full px-layout-col flex-col flex gap-y-16 justify-end py-16'>
                                <div
                                    className='w-3/7'>
                                    <LinkBlock {...props.heading3?.[0]}/>
                                    <div dangerouslySetInnerHTML={{ __html: props.column3 }} />
                                </div>
                                <div dangerouslySetInnerHTML={{ __html: props.column4 }} />
                                <div dangerouslySetInnerHTML={{ __html: props.column5 }} />
                            </div>
                        </div>
                    </div>
                </HeaderDialog>
            </div>

            {(isFixed || !props.isHomepage) && (
                <nav
                    className={`${!props.isHomepage && !isFixed ? 'opacity-0' : ''} group sticky bg-white hidden sm:block top-0 w-full z-30 overflow-x-hidden`}
                    ref={navRef}>
                    <div className='-mx-layout-col py-8 max-h-36 overflow-hidden transition-all duration-300 ease-in group-hover:max-h-[100px]'>
                        <div
                            className='flex w-full items-start px-layout-outer'>
                            <div
                                className='w-1/7 px-layout-col flex flex-col'>
                                <Button
                                    as='a'
                                    className='flex self-start hover:text-text-default-secondary'
                                    href={`/?office=${props.office || 'london'}`}>
                                    {offices.find(office => office.value === props.office)?.label || 'Home'}
                                </Button>
                                {offices.map(office => {
                                    if (office.value === props.office) { return null; }
                                    return (
                                        <a
                                            className='opacity-0 group-hover:opacity-100 hover:text-text-default-secondary'
                                            href={`/?office=${office.value}`}
                                            key={office.value}>
                                            {office.label}
                                        </a>
                                    );
                                })}
                            </div>
                            <div className='w-1/7 px-layout-col'>
                                <LinkBlock {...props.heading1?.[0]}/>
                                <div
                                    className='flex-grow opacity-0 group-hover:opacity-100'
                                    dangerouslySetInnerHTML={{ __html: props.column1 }}/>
                            </div>
                            <div className='w-1/7 px-layout-col'>
                                <LinkBlock {...props.heading2?.[0]}/>
                                <div
                                    className='flex-grow opacity-0 group-hover:opacity-100'
                                    dangerouslySetInnerHTML={{ __html: props.column2 }}/>
                            </div>
                            <div className='w-2/7 px-layout-col -mt-4'>
                                <input
                                    className='w-full type-style-s placeholder:text-text-default-primary rounded-none'
                                    onKeyDown={handleSearch}
                                    placeholder='Search'/>
                            </div>
                            <div className='w-2/7 px-layout-col'>
                                <LinkBlock {...props.heading3?.[0]}/>
                                <div
                                    className='flex-grow opacity-0 group-hover:opacity-100'
                                    dangerouslySetInnerHTML={{ __html: props.column3 }}/>
                            </div>
                        </div>
                    </div>
                </nav>
            )}

            {props.isHomepage && (
                <header
                    className={`${isHidden ? '' : 'z-10'} h-[415px] bg-white hidden sm:flex flex-col justify-between sticky top-0 py-8 w-full overflow-x-hidden`}
                    ref={headerRef}>
                    <div className='-mx-layout-col bg-white'>
                        <div className='flex w-full px-layout-outer'>
                            <div
                                className='w-1/7 px-layout-col'>
                                <Button
                                    as='a'
                                    className='flex self-start hover:text-text-default-secondary'
                                    href={`/?office=${props.office || 'london'}`}>
                                    {offices.find(office => office.value === props.office)?.label || 'London'}
                                </Button>
                                <div
                                    className='flex-grow flex-col flex'
                                    ref={el => itemRefs.current[0] = el}>
                                    {offices.map(office => {
                                        if (office.value === props.office) { return null; }
                                        return (
                                            <a
                                                className='hover:text-text-default-secondary'
                                                href={`/?office=${office.value}`}>
                                                {office.label}
                                            </a>
                                        );
                                    })}
                                </div>
                            </div>
                            <div className='w-1/7 px-layout-col flex flex-col'>
                                <LinkBlock {...props.heading1?.[0]}/>
                                <div
                                    className='flex-grow'
                                    dangerouslySetInnerHTML={{ __html: props.column1 }}
                                    ref={el => itemRefs.current[1] = el}/>
                            </div>
                            <div className='w-1/7 px-layout-col flex flex-col'>
                                <LinkBlock {...props.heading2?.[0]}/>
                                <div
                                    className='flex-grow'
                                    dangerouslySetInnerHTML={{ __html: props.column2 }}
                                    ref={el => itemRefs.current[2] = el}/>
                            </div>
                            <div className='w-2/7 px-layout-col -mt-4'>
                                <input
                                    className='w-full type-style-s placeholder:text-text-default-primary rounded-none'
                                    onKeyDown={handleSearch}
                                    placeholder='Search'/>
                            </div>
                            <div className='w-1/7 px-layout-col flex flex-col'>
                                <LinkBlock {...props.heading3?.[0]}/>
                                <div
                                    className='flex-grow'
                                    dangerouslySetInnerHTML={{ __html: props.column3 }}
                                    ref={el => itemRefs.current[3] = el}/>
                            </div>
                        </div>
                    </div>
                    {!isHidden && (
                        <div className='px-layout-outer'>
                            <IconLogo className='hidden sm:flex w-full'/>
                            <IconLogoMobile className='sm:hidden w-full'/>
                        </div>
                    )}
                </header>
            )}
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default Header;
